import { useCallback, useEffect, useReducer, useState } from "react";
import truncate from "lodash/truncate";
import { NotificationsLevel } from "../lib/enums";
import { route, settings } from "../shim/jsbridge";
import { show } from "../notifications/notifications";
import {
  UPLOAD_ACTIONS,
  uploadReducer,
  uploadState
} from "../modules/header/upload.reducer";
import api from "../services/api";
import { getPathParts } from "../modules/header/utils";
import useUploadNotifier from "./upload/useUploadNotifier";

const { max_file_upload_size: MAX_UPLOAD_FILE_SIZE_IN_MB } = settings || {};
const maxFileSize = MAX_UPLOAD_FILE_SIZE_IN_MB * (1024 * 1024);
const maxFileSizeDeepScan = 10000 * (1024 * 1024);

const useSampleUpload = () => {
  const [state, dispatch] = useReducer(uploadReducer, uploadState);
  const [upload, setUpload] = useState({
    files: [],
    password: undefined,
    uploadToTicloud: undefined,
    rlCloudSandboxPlatform: undefined,
    rlCloudSandboxGeolocation: undefined,
    rlCloudSandboxLocale: undefined,
    rlCloudSandboxInteractive: undefined,
    dynamicAnalysis: undefined
  });
  const {
    files,
    password,
    uploadToTicloud,
    rlCloudSandboxPlatform,
    rlCloudSandboxGeolocation,
    rlCloudSandboxLocale,
    rlCloudSandboxInteractive,
    dynamicAnalysis
  } = upload || {};

  const { notifyUpload } = useUploadNotifier();

  const createControllers = fls =>
    fls.reduce(
      (acc, file) => ({
        ...acc,
        [file.name]: new AbortController()
      }),
      {}
    );

  const uploadSamples = useCallback(() => {
    if (!files.length) return;

    if (MAX_UPLOAD_FILE_SIZE_IN_MB < 0) {
      return;
    }
    const showDeepScanPromo = files.some(
      item => item.size >= maxFileSize && item.size < maxFileSizeDeepScan
    );
    const showMaxFileSizeNotification = files.some(
      item => item.size > maxFileSizeDeepScan
    );
    if (showDeepScanPromo) {
      window.tcbase.showMaxFileSizeModal();
    } else if (showMaxFileSizeNotification) {
      show({
        level: NotificationsLevel.WARNING,
        message: `Some file(s) exceed the recommended maximum file size (${MAX_UPLOAD_FILE_SIZE_IN_MB}MB) supported for analysis.
            Please contact support for options.`
      });
    }
    const validFiles = files.filter(file => maxFileSize > file.size);
    show({
      level: NotificationsLevel.INFO,
      message: `Submitting ${validFiles.length} file`.pluralize(
        validFiles.length
      )
    });
    const controllers = createControllers(validFiles);
    const reduced = validFiles.reduce(
      (acc, file) => ({
        ...acc,
        [file.name]: {
          name: file.name,
          size: file.size,
          progress: 0
        }
      }),
      {}
    );
    dispatch({
      type: UPLOAD_ACTIONS.SETUP,
      count: validFiles.length,
      controllers,
      status: reduced
    });
    validFiles.forEach(file => {
      api
        .uploadSample(
          file,
          password,
          uploadToTicloud,
          rlCloudSandboxPlatform,
          rlCloudSandboxGeolocation,
          rlCloudSandboxLocale,
          rlCloudSandboxInteractive,
          dynamicAnalysis?.join(","),
          progressEvent => {
            dispatch({
              type: UPLOAD_ACTIONS.UPDATE_PROGRESS,
              status: {
                [file.name]: {
                  name: file.name,
                  size: file.size,
                  progress: Math.round(
                    (100 * progressEvent.loaded) / progressEvent.total
                  )
                }
              }
            });
          },
          controllers[file.name].signal
        )
        .then(({ data }) => {
          const { message, uploadedSha1s } = data || {};

          if (!uploadedSha1s.length) {
            show({
              level: NotificationsLevel.ERROR,
              message
            });
          } else {
            notifyUpload();
          }

          return { uploadedSha1s };
        })
        .then(({ uploadedSha1s }) => {
          if (rlCloudSandboxInteractive) {
            uploadedSha1s?.forEach(requestedHash =>
              window.open(
                route("rl_interactive_analysis_for_hash", [requestedHash]),
                "_blank"
              )
            );
          }
        })
        .catch(err => {
          const { message } = err?.data || {};
          if (err.problem === "CANCEL_ERROR") {
            dispatch({ type: UPLOAD_ACTIONS.CANCEL_UPLOAD, status: file });
            return;
          }
          show({
            level: NotificationsLevel.ERROR,
            message: `${truncate(file.name, {
              length: 32
            })} upload failed${message ? `: ${message}` : ""}`
          });
        })
        .finally(() => dispatch({ type: UPLOAD_ACTIONS.DECREASE }));
    });
  }, [files, password, uploadToTicloud, rlCloudSandboxPlatform, notifyUpload]);

  useEffect(() => {
    // reset states
    if (!state.uploading) {
      setUpload({
        files: [],
        password: undefined,
        uploadToTicloud: undefined,
        rlCloudSandboxPlatform: undefined,
        rlCloudSandboxGeolocation: undefined,
        rlCloudSandboxLocale: undefined,
        rlCloudSandboxInteractive: undefined,
        dynamicAnalysis: undefined
      });
      dispatch({ type: UPLOAD_ACTIONS.RESET });
    }
  }, [state.uploading]);

  useEffect(() => {
    uploadSamples();
  }, [uploadSamples]);

  return [state, setUpload];
};

export default useSampleUpload;
